import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity as ActivityIcon,
  AlertOctagon,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  DollarSign,
  Inbox,
  Mail,
  Smartphone,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useRealtime } from "@/lib/realtime";

export function Home() {
  const draftsQ = useQuery({
    queryKey: ["drafts"],
    queryFn: () => api.drafts({ limit: 100 }),
    refetchInterval: 15_000,
  });
  const triageQ = useQuery({
    queryKey: ["triage"],
    queryFn: () => api.triage({ limit: 50 }),
    refetchInterval: 15_000,
  });
  const activityQ = useQuery({
    queryKey: ["activity-home"],
    queryFn: () => api.activityFeed({ limit: 20 }),
    refetchInterval: 8_000,
  });
  const healthQ = useQuery({
    queryKey: ["system-health"],
    queryFn: () => api.systemHealth(),
    refetchInterval: 15_000,
  });
  const recruitersQ = useQuery({
    queryKey: ["recruiters-home"],
    queryFn: () => api.recruitersList({ limit: 100 }),
    refetchInterval: 30_000,
  });
  const fleetQ = useQuery({
    queryKey: ["fleet-home"],
    queryFn: () => api.fleetStatus(),
    refetchInterval: 30_000,
  });

  useRealtime("drafts_pending", () => draftsQ.refetch());
  useRealtime("events", () => activityQ.refetch());

  // Compute KPIs
  const drafts = draftsQ.data ?? [];
  const activity = activityQ.data ?? [];
  const errors24h = activity.filter((a) => a.severity === "urgent").length;
  const spendToday = activity.reduce((acc, a) => acc + (a.cost_cents || 0), 0);
  const sendsToday = activity.filter(
    (a) =>
      a.event_type === "job.completed" &&
      (a.label || "").toLowerCase().includes("worker"),
  ).length;
  const recruiters = recruitersQ.data ?? [];
  const onboardedRecruiters = recruiters.filter((r) => r.is_onboarded);
  const recruitersDmsToday = recruiters.reduce((acc, r) => acc + (r.dms_yday || 0), 0);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Home</h1>
        <p className="text-sm text-muted-foreground">
          Today at a glance. Polled every 8-30s.
        </p>
      </header>

      {/* KPI strip */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-6">
        <KpiCard
          icon={<Mail className="h-4 w-4" />}
          label="Drafts pending"
          value={String(drafts.length)}
          tone={drafts.length > 0 ? "warn" : "neutral"}
          href="/drafts"
        />
        <KpiCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Triage items"
          value={String(triageQ.data?.length ?? 0)}
          tone={(triageQ.data?.length ?? 0) > 0 ? "warn" : "neutral"}
          href="/triage"
        />
        <KpiCard
          icon={<Smartphone className="h-4 w-4" />}
          label="Fleet online"
          value={
            fleetQ.data
              ? `${fleetQ.data.summary.online_devices}/${fleetQ.data.summary.total_devices}`
              : "—"
          }
          tone={
            fleetQ.data && fleetQ.data.summary.offline_devices > 0
              ? "warn"
              : "neutral"
          }
          href="/fleet"
        />
        <KpiCard
          icon={<Zap className="h-4 w-4" />}
          label="Fleet sent (today)"
          value={String(fleetQ.data?.summary.total_sent_today ?? "—")}
          href="/fleet"
        />
        <KpiCard
          icon={<AlertOctagon className="h-4 w-4" />}
          label="Errors (24h)"
          value={String(errors24h)}
          tone={errors24h > 0 ? "danger" : "neutral"}
          href="/activity"
        />
        <KpiCard
          icon={<DollarSign className="h-4 w-4" />}
          label="Spend (24h)"
          value={`$${(spendToday / 100).toFixed(2)}`}
          href="/costs"
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Drafts pending */}
        <section className="space-y-2">
          <SectionHeader
            label="Needs your approval"
            count={drafts.length}
            href="/drafts"
            icon={<Mail className="h-4 w-4" />}
          />
          {draftsQ.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : drafts.length === 0 ? (
            <EmptyMini
              icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              text="Inbox zero. Nothing waiting."
            />
          ) : (
            <div className="space-y-2">
              {drafts.slice(0, 3).map((d) => (
                <Link
                  key={d.draft_id}
                  to="/drafts"
                  className="block rounded-md border bg-card p-3 hover:bg-muted/30 text-sm"
                >
                  <div className="flex items-baseline justify-between text-2xs text-muted-foreground mb-0.5">
                    <span>
                      <span className="font-medium text-foreground">{d.creator_handle}</span>
                      {" · "}{d.kind}
                    </span>
                    <span>{d.member_phone_redacted}</span>
                  </div>
                  <div className="line-clamp-2 text-foreground">
                    {d.chunks[0] ?? d.inbound_message ?? "(empty)"}
                  </div>
                </Link>
              ))}
              {drafts.length > 3 && (
                <Link
                  to="/drafts"
                  className="block text-center text-xs text-muted-foreground hover:text-foreground py-1"
                >
                  + {drafts.length - 3} more →
                </Link>
              )}
            </div>
          )}
        </section>

        {/* Recruiters status */}
        <section className="space-y-2">
          <SectionHeader
            label="Recruiter team"
            count={onboardedRecruiters.length}
            href="/recruiters"
            icon={<Users className="h-4 w-4" />}
          />
          {recruitersQ.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : onboardedRecruiters.length === 0 ? (
            <EmptyMini
              icon={<TrendingUp className="h-4 w-4 text-amber-600" />}
              text="No recruiters onboarded yet. Click Onboard on /recruiters."
            />
          ) : (
            <div className="space-y-1.5">
              {onboardedRecruiters.slice(0, 5).map((r) => {
                const hit = (r.dms_yday || 0) >= (r.daily_dm_quota || 30);
                return (
                  <Link
                    key={r.recruiter_id}
                    to="/recruiters"
                    className="flex items-baseline justify-between rounded-md border bg-card p-2 hover:bg-muted/30 text-sm"
                  >
                    <span className="font-medium">{r.name}</span>
                    <span className={hit ? "text-emerald-600 text-xs" : "text-amber-700 text-xs"}>
                      {r.dms_yday || 0}/{r.daily_dm_quota || 30}
                      {r.streak_misses > 0 ? ` · streak ${r.streak_misses}d` : ""}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Activity */}
      <section className="space-y-2">
        <SectionHeader
          label="Live activity"
          count={activity.length}
          href="/activity"
          icon={<ActivityIcon className="h-4 w-4" />}
        />
        {activityQ.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : activity.length === 0 ? (
          <EmptyMini text="Nothing in the last 24 hours." />
        ) : (
          <div className="rounded-md border bg-card divide-y">
            {activity.slice(0, 10).map((a) => (
              <div
                key={a.id}
                className="flex items-baseline gap-3 px-3 py-2 text-sm hover:bg-muted/30"
              >
                <span
                  className={
                    "h-1.5 w-1.5 rounded-full mt-1.5 " +
                    (a.severity === "urgent"
                      ? "bg-rose-500"
                      : a.severity === "warn"
                      ? "bg-amber-500"
                      : "bg-zinc-400")
                  }
                />
                <span className="font-medium text-xs">{a.label}</span>
                <span className="flex-1 truncate text-xs text-muted-foreground">
                  {a.preview}
                </span>
                <span className="text-2xs font-mono text-muted-foreground">
                  {a.occurred_at ? a.occurred_at.slice(11, 19) : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Bridge health */}
      <section className="space-y-2">
        <SectionHeader
          label="Bridges"
          count={(healthQ.data?.bridges || []).length}
          href="/system"
          icon={<Inbox className="h-4 w-4" />}
        />
        {!healthQ.data ? (
          <Skeleton className="h-12 w-full" />
        ) : healthQ.data.bridges.length === 0 ? (
          <EmptyMini text="No bridges connected." />
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {healthQ.data.bridges.map((b) => (
              <div
                key={b.host_id}
                className="flex items-baseline justify-between rounded-md border bg-card p-2 text-sm"
              >
                <span className="font-mono text-xs">{b.host_id}</span>
                <span className={b.status === "healthy" ? "text-emerald-600 text-xs" : "text-rose-600 text-xs"}>
                  {b.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function KpiCard({
  icon, label, value, tone = "neutral", href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "neutral" | "warn" | "danger";
  href?: string;
}) {
  const accent =
    tone === "danger"
      ? "text-rose-600"
      : tone === "warn"
      ? "text-amber-700"
      : "";
  const inner = (
    <div className="rounded-md border bg-card p-3 hover:bg-muted/30 transition-colors">
      <div className="flex items-center gap-1.5 text-2xs uppercase text-muted-foreground">
        {icon} {label}
      </div>
      <div className={`text-xl font-semibold mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

function SectionHeader({
  label, count, href, icon,
}: {
  label: string;
  count: number;
  href: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between">
      <h3 className="text-sm font-medium flex items-center gap-1.5">
        {icon} {label}
        <span className="text-xs text-muted-foreground">({count})</span>
      </h3>
      <Link to={href} className="text-xs text-muted-foreground hover:text-foreground">
        view all <ArrowRight className="inline h-3 w-3" />
      </Link>
    </div>
  );
}

function EmptyMini({
  text, icon,
}: {
  text: string;
  icon?: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-dashed bg-card/30 p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
      {icon} {text}
    </div>
  );
}
